// Renders a todo description (the markdown dialect of `bot/markdown_entities.py`) to HTML for `MarkdownView.vue`.
//
// Raw HTML in the source is never passed through (`html: false` escapes it); the only tags that work are the ones
// the backend itself writes for things markdown cannot say, matched by the rules below with strict patterns:
// `<ins>`, `<tg-spoiler>`, `<tg-time unix format>text</tg-time>` and `<blockquote [expandable]>…</blockquote>`.
// Images are never loaded from the markdown: a `tg://time` image is a date, any other image becomes its alt text.

import MarkdownIt from 'markdown-it'
import type { MarkdownIt as Renderer, StateBlock, StateInline } from 'markdown-it'

const ALLOWED_LINK_PROTOCOLS = /^(https?|tg|mailto):/i
const TG_TIME_SRC = /^tg:\/\/time\?(?:[^#]*&)?unix=(\d{1,12})(?:&|$)/
const TG_TIME_FORMAT = /[?&]format=([A-Za-z]{1,8})(?:&|$)/
const TG_TIME_TAG = /^<tg-time unix="(\d{1,12})"(?: format="([A-Za-z]{1,8})")?>([^<]*)<\/tg-time>/
const SIMPLE_TAGS: Record<string, [string, boolean]> = {
  '<ins>': ['ins', true],
  '</ins>': ['ins', false],
  '<tg-spoiler>': ['spoiler', true],
  '</tg-spoiler>': ['spoiler', false],
}

function spoilerRule(state: StateInline, silent: boolean): boolean {
  const start = state.pos
  if (state.src.slice(start, start + 2) !== '||') return false
  const end = state.src.indexOf('||', start + 2)
  if (end === -1 || end === start + 2 || end >= state.posMax) return false
  if (!silent) {
    state.push('spoiler_open', 'span', 1).markup = '||'
    const oldMax = state.posMax
    state.pos = start + 2
    state.posMax = end
    state.md.inline.tokenize(state)
    state.posMax = oldMax
    state.push('spoiler_close', 'span', -1).markup = '||'
  }
  state.pos = end + 2
  return true
}

function allowedTagRule(state: StateInline, silent: boolean): boolean {
  if (state.src.charCodeAt(state.pos) !== 0x3c /* < */) return false
  const rest = state.src.slice(state.pos, state.posMax)
  const time = TG_TIME_TAG.exec(rest)
  if (time) {
    if (!silent) {
      const token = state.push('tg_time', 'time', 0)
      token.attrSet('unix', time[1])
      token.attrSet('format', time[2] ?? '')
      token.content = time[3]
    }
    state.pos += time[0].length
    return true
  }
  const tag = /^<\/?[a-z-]+>/.exec(rest)?.[0]
  const simple = tag ? SIMPLE_TAGS[tag] : undefined
  if (!tag || !simple) return false
  if (!silent) {
    const [name, opening] = simple
    state.push(`${name}_${opening ? 'open' : 'close'}`, name === 'ins' ? 'ins' : 'span', opening ? 1 : -1)
  }
  state.pos += tag.length
  return true
}

// `<blockquote expandable>` / `<blockquote>` on a line of its own up to the `</blockquote>` line: a quote whose
// content is markdown again (the backend writes it for Telegram's expandable quote).
function blockquoteTagRule(state: StateBlock, startLine: number, endLine: number, silent: boolean): boolean {
  if (state.sCount[startLine] - state.blkIndent >= 4) return false
  const lineText = (line: number): string =>
    state.src.slice(state.bMarks[line] + state.tShift[line], state.eMarks[line])
  if (!/^<blockquote(?: expandable)?>$/.test(lineText(startLine).trim())) return false
  let close = startLine + 1
  while (close < endLine && lineText(close).trim() !== '</blockquote>') close++
  if (close >= endLine) return false
  if (silent) return true
  const open = state.push('blockquote_open', 'blockquote', 1)
  open.map = [startLine, close + 1]
  const oldParent = state.parentType
  state.parentType = 'blockquote'
  state.md.block.tokenize(state, startLine + 1, close)
  state.parentType = oldParent
  state.push('blockquote_close', 'blockquote', -1)
  state.line = close + 1
  return true
}

function createMarkdownIt(): Renderer {
  const md = new MarkdownIt('commonmark', { html: false, linkify: false, breaks: false })
  md.enable('strikethrough')
  md.inline.ruler.before('text', 'spoiler', spoilerRule)
  md.inline.ruler.before('escape', 'allowed_tag', allowedTagRule)
  md.block.ruler.before('blockquote', 'blockquote_tag', blockquoteTagRule, { alt: ['paragraph', 'reference', 'blockquote', 'list'] })
  // `|` and `<` end a text run, so the rules above get a chance to start there.
  md.inline.ruler.at('text', (state, silent) => {
    let pos = state.pos
    while (pos < state.posMax && !TERMINATORS.has(state.src.charCodeAt(pos))) pos++
    if (pos === state.pos) return false
    if (!silent) state.pending += state.src.slice(state.pos, pos)
    state.pos = pos
    return true
  })

  md.validateLink = (url: string): boolean => ALLOWED_LINK_PROTOCOLS.test(url.trim()) || /^(\/|#)/.test(url.trim())

  const escape = md.utils.escapeHtml
  md.renderer.rules.spoiler_open = () => '<span class="md-spoiler" data-spoiler>'
  md.renderer.rules.spoiler_close = () => '</span>'
  md.renderer.rules.ins_open = () => '<ins>'
  md.renderer.rules.ins_close = () => '</ins>'
  md.renderer.rules.tg_time = (tokens, idx) => timeHtml(String(tokens[idx].attrGet('unix')), String(tokens[idx].attrGet('format')), tokens[idx].content)
  md.renderer.rules.image = (tokens, idx) => {
    const token = tokens[idx]
    const src = String(token.attrGet('src') ?? '')
    const unix = TG_TIME_SRC.exec(src)?.[1]
    if (unix) return timeHtml(unix, TG_TIME_FORMAT.exec(src)?.[1] ?? '', token.content)
    return escape(token.content)
  }
  md.renderer.rules.link_open = (tokens, idx) => {
    const href = String(tokens[idx].attrGet('href') ?? '')
    return `<a href="${escape(href)}" data-href="${escape(href)}" rel="noopener noreferrer">`
  }
  return md
}

const TERMINATORS = new Set(
  [...'\n!#$%&*+-:<=>@[\\]^_`{}~|'].map((char) => char.charCodeAt(0)),
)

function timeHtml(unix: string | null, format: string | null, fallback: string): string {
  const escape = md.utils.escapeHtml
  return `<time data-unix="${escape(unix ?? '')}" data-format="${escape(format ?? '')}">${escape(fallback)}</time>`
}

const md = createMarkdownIt()

export function renderMarkdown(source: string): string {
  return md.render(source)
}
